import { useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, Download, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { MARKSHEET_YEARS } from "../../data/site";

const inputCls = "w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30";
const labelCls = "block text-xs uppercase tracking-widest text-forest/60 mb-1.5";
const empty = { year: "2020", document: "Marksheet", name: "", abc_id: "", available: false, collected: false };

const Tick = ({ on, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`w-6 h-6 rounded border flex items-center justify-center transition-colors ${on ? "bg-forest border-forest text-bone" : "border-line text-transparent hover:border-forest"}`}>
    <Check size={14} />
  </button>
);

function parseCSV(text) {
  const rows = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return rows;
  const header = lines[0].toLowerCase();
  const start = header.includes("name") && header.includes("year") ? 1 : 0;
  const truthy = (v) => ["1", "yes", "true", "y"].includes(String(v || "").trim().toLowerCase());
  for (let i = start; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => c.trim());
    if (!cols[2]) continue;
    rows.push({ year: cols[0] || "2020", document: cols[1] || "Marksheet", name: cols[2] || "", abc_id: cols[3] || "", available: truthy(cols[4]), collected: truthy(cols[5]) });
  }
  return rows;
}

export default function MarksheetManager() {
  const { marksheets, reload } = useSite();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("All");

  const shown = filter === "All" ? marksheets : marksheets.filter((m) => m.year === filter);

  const openNew = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (m) => { setEditing(m.id); setForm({ year: m.year || "2020", document: m.document || "Marksheet", name: m.name || "", abc_id: m.abc_id || "", available: !!m.available, collected: !!m.collected }); setModal(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.update("marksheets", editing, form);
      else await api.create("marksheets", form);
      toast.success(editing ? "Updated" : "Added");
      setModal(false);
      await reload();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const toggle = async (m, field) => {
    try { await api.update("marksheets", m.id, { ...m, [field]: !m[field] }); await reload(); }
    catch { toast.error("Failed to update"); }
  };

  const remove = async (id) => {
    try { await api.remove("marksheets", id); toast.success("Deleted"); await reload(); }
    catch { toast.error("Failed to delete"); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const rows = parseCSV(await file.text());
      if (!rows.length) { toast.error("No valid rows in CSV"); return; }
      for (const r of rows) await api.create("marksheets", r);
      toast.success(`Imported ${rows.length} record(s)`);
      await reload();
    } catch { toast.error("Import failed"); }
    finally { e.target.value = ""; }
  };

  const onDownload = () => {
    const header = "year,document,name,abc_id,available,collected\n";
    const body = marksheets.map((m) => [m.year, m.document, m.name, m.abc_id, m.available ? "yes" : "no", m.collected ? "yes" : "no"].map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "marksheets.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="marksheet-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-serif text-2xl text-forest">{marksheets.length} Marksheet Record{marksheets.length !== 1 ? "s" : ""}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-2 border border-forest text-forest px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors cursor-pointer">
            <Upload size={14} /> Bulk Import
            <input data-testid="import-marksheets-input" type="file" accept=".csv,text/csv" onChange={onImport} className="hidden" />
          </label>
          <button data-testid="download-marksheets-btn" onClick={onDownload} className="inline-flex items-center gap-2 border border-forest text-forest px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors">
            <Download size={14} /> Download
          </button>
          <button data-testid="add-marksheet-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-gold text-forest px-5 py-2.5 rounded-full text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
            <Plus size={14} /> Add Record
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["All", ...MARKSHEET_YEARS].map((y) => (
          <button key={y} data-testid={`marksheet-filter-${y.replace(/\s/g, "-")}`} onClick={() => setFilter(y)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === y ? "bg-forest text-bone border-forest" : "border-line text-muted hover:border-forest"}`}>{y}</button>
        ))}
      </div>

      <p className="text-xs text-muted mb-4">CSV columns: <span className="font-mono">year, document, name, abc_id, available, collected</span> (available/collected = yes/no)</p>

      <div className="border border-line bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone text-forest/60 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Year</th>
              <th className="text-left px-4 py-3">Document</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">ABC ID</th>
              <th className="text-center px-4 py-3">Available</th>
              <th className="text-center px-4 py-3">Collected</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {shown.map((m) => (
              <tr key={m.id} data-testid="marksheet-row">
                <td className="px-4 py-3 text-muted">{m.year}</td>
                <td className="px-4 py-3 text-forest">{m.document || "Marksheet"}</td>
                <td className="px-4 py-3 font-medium text-forest">{m.name}</td>
                <td className="px-4 py-3 font-mono text-forest">{m.abc_id}</td>
                <td className="px-4 py-3"><div className="flex justify-center"><Tick on={m.available} onClick={() => toggle(m, "available")} testid="toggle-available-btn" /></div></td>
                <td className="px-4 py-3"><div className="flex justify-center"><Tick on={m.collected} onClick={() => toggle(m, "collected")} testid="toggle-collected-btn" /></div></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    <button data-testid="edit-marksheet-btn" onClick={() => openEdit(m)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone transition-colors"><Pencil size={15} /></button>
                    <button data-testid="delete-marksheet-btn" onClick={() => remove(m.id)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white transition-colors"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <p className="p-8 text-muted">No marksheet records yet. Add one or bulk-import a CSV.</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <form data-testid="marksheet-form" onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-bone w-full max-w-lg rounded-sm border border-line p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-3xl text-forest">{editing ? "Edit" : "Add"} Marksheet</h3>
              <button type="button" onClick={() => setModal(false)} className="text-muted hover:text-forest"><X size={22} /></button>
            </div>
            <div className="mb-5">
              <label className={labelCls}>Year</label>
              <select data-testid="marksheet-field-year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className={inputCls}>
                {MARKSHEET_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="mb-5">
              <label className={labelCls}>Document</label>
              <input data-testid="marksheet-field-document" value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} placeholder="e.g. Marksheet, Migration Certificate" className={inputCls} />
            </div>
            <div className="mb-5">
              <label className={labelCls}>Name of Student</label>
              <input data-testid="marksheet-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} />
            </div>
            <div className="mb-5">
              <label className={labelCls}>ABC ID</label>
              <input data-testid="marksheet-field-abc" value={form.abc_id} onChange={(e) => setForm({ ...form, abc_id: e.target.value })} className={inputCls} />
            </div>
            <div className="flex gap-6 mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="marksheet-field-available" type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} className="w-4 h-4 accent-forest" />
                <span className="text-sm text-forest">Marksheet Available</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="marksheet-field-collected" type="checkbox" checked={form.collected} onChange={(e) => setForm({ ...form, collected: e.target.checked })} className="w-4 h-4 accent-forest" />
                <span className="text-sm text-forest">Collected</span>
              </label>
            </div>
            <button data-testid="save-marksheet-btn" type="submit" disabled={saving} className="w-full bg-forest text-bone py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
